import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { toast } from "react-toastify";
import { useSelector, useDispatch } from "react-redux";
import "react-toastify/dist/ReactToastify.css";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ReactTooltip from "react-tooltip";
import {
  Box,
  IconButton,
  InputBase,
  Typography,
  useTheme,
  Grid,
} from "@mui/material";
import { tokens } from "../theme";
import AddCircleOutlinedIcon from "@mui/icons-material/AddCircleOutlined";
import RemoveCircleOutlinedIcon from "@mui/icons-material/RemoveCircleOutlined";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { addToMiscCartItems } from "../features/cart/cartSlice";
import { generatedUniqueId } from "./utils";

const CreateMiscPart = (props) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const dispatch = useDispatch();
  const currentActiveSection = useSelector(
    (state) => state.cart.sections.currentActive
  );

  const custom = props.custom;

  // All State
  const [partName, setPartName] = useState("");
  const [partDescription, setPartDescription] = useState("");
  const [partDimensions, setPartDimensions] = useState("• "); // Quill HTML
  const [partFinish, setPartFinish] = useState("");
  const [partPrice, setPartPrice] = useState("");
  const [partComments, setPartComments] = useState("");
  const [partDuty, setPartDuty] = useState("");
  const [quantity, setQuantity] = useState(0);
  const [margin, setMargin] = useState("");
  const [externalVendorCode, setExternalVendorCode] = useState("");
  const [itemQuantityAvailable, setItemQuantityAvailable] = useState();

  // Methods
  const incremet = () => {
    setQuantity(quantity + 1);
    setItemQuantityAvailable(itemQuantityAvailable - 1);
  };

  const decrement = () => {
    setQuantity(quantity - 1);
    setItemQuantityAvailable(itemQuantityAvailable + 1);
  };

  const totalPrice = useMemo(() => {
    if (margin === "") {
      return partPrice * quantity;
    } else {
      return partPrice * quantity * (1 + parseInt(margin) / 100);
    }
  }, [quantity, margin, partPrice]);

  // Quill Enter Key Handler
  const quillRef = useRef(null);
  const handleQuillKeyDown = useCallback((e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      const editor = quillRef?.current?.getEditor();
      const range = editor?.getSelection(true);
      if (range) {
        editor.insertText(range.index, "\n• ");
        editor.setSelection(range.index + 3);
      }
    }
  }, []);

  useEffect(() => {
    const editor = quillRef?.current?.getEditor();
    if (editor) {
      editor.root.addEventListener("keydown", handleQuillKeyDown);
    }

    return () => {
      if (editor) {
        editor.root.removeEventListener("keydown", handleQuillKeyDown);
      }
    };
  }, [handleQuillKeyDown]);

  const addToCartHandler = () => {
    const item = {
      id: generatedUniqueId(),
      part: {
        name: custom ? partName : "",
        description: !custom ? partName : "",
        dimensions: partDimensions, // Save full HTML!
        duty: partDuty,
        finish: partFinish,
        price: partPrice,
        comments: partComments,
      },
      quantity: quantity,
      price: totalPrice,
      section: currentActiveSection,
      margin: margin,
      custom: props.custom,
      externalVendorCode: externalVendorCode,
    };
    dispatch(addToMiscCartItems(item));

    // Reset
    setPartName("");
    setPartDimensions("• ");
    setPartDuty("");
    setPartFinish("");
    setPartPrice("");
    setPartComments("");
    setQuantity(0);
    toast.success("Part Successfully Added To Cart!");
  };

  return (
    <Box
      gridColumn="span 6"
      gridRow="span 5"
      backgroundColor={colors.blueAccent[800]}
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
      pb="0.5em"
      p={3}
      borderRadius="8px"
    >
      <Typography
        fontWeight="700"
        my="0.25em"
        mx="0.25em"
        color={colors.blueAccent[200]}
      >
        {`Create a Custom ${!custom ? "Misc" : ""} Item`}
      </Typography>

      <Grid container spacing={2} sx={{ width: "100%", flex: 1 }}>
        <Grid item xs={12}>
          <InputBase
            placeholder="Name"
            fullWidth
            className="outlined border rounded my-1"
            value={partName}
            onChange={(e) => setPartName(e.target.value)}
          />
        </Grid>

        {/* Rich Text Input */}
        <Grid item xs={12}>
          <Typography
            fontWeight={600}
            fontSize="0.9rem"
            color={colors.grey[100]}
            mb="0.25em"
          >
            Dimensions / Description
          </Typography>
          <Box
            sx={{
              backgroundColor: colors.primary[400],
              borderRadius: "8px",
              "& .ql-toolbar": {
                backgroundColor: colors.primary[300],
                borderTopLeftRadius: "8px",
                borderTopRightRadius: "8px",
                border: `1px solid ${colors.grey[400]}`,
              },
              "& .ql-container": {
                backgroundColor: colors.primary[200],
                borderBottomLeftRadius: "8px",
                borderBottomRightRadius: "8px",
                border: `1px solid ${colors.grey[400]}`,
                height: "100px",
                display: "flex",
                flexDirection: "column",
              },
              "& .ql-editor": {
                color: colors.grey[100],
                height: "100%",
                overflowY: "auto",
                flex: 1,
                paddingBottom: "1rem"
              },
            }}
          >
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={partDimensions}
              onChange={setPartDimensions}
              placeholder="Use bullets, underline, bold, etc."
              modules={{
                toolbar: [
                  ["bold", "italic", "underline"],
                  [{ list: "bullet" }],
                  ["clean"],
                ],
              }}
            />
          </Box>
        </Grid>

        {/* Other Inputs */}
        {[
          { label: "Duty", value: partDuty, setValue: setPartDuty },
          { label: "Finish", value: partFinish, setValue: setPartFinish },
          { label: "Price", value: partPrice, setValue: setPartPrice, type: "number" },
          { label: "Margin", value: margin, setValue: setMargin },
          { label: "Ext Vendor Code", value: externalVendorCode, setValue: setExternalVendorCode },
          { label: "Comments", value: partComments, setValue: setPartComments },
        ].map((field, i) => (
          <Grid item xs={12} key={i}>
            <InputBase
              type={field.type || "text"}
              placeholder={field.label}
              fullWidth
              className="outlined border rounded my-1"
              value={field.value}
              onChange={(e) => field.setValue(e.target.value)}
            />
          </Grid>
        ))}
      </Grid>

      {/* Add to Cart Bottom Row */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-around"
        alignItems="center"
        width="100%"
        mt={2}
      >
        {/* Quantity Control */}
        <div
          className="d-flex align-items-center justify-content-between position-relative border rounded-pill"
          style={{ minWidth: 120 }}
        >
          <IconButton
            onClick={decrement}
            disabled={!partName || !partPrice || quantity <= 0}
          >
            <RemoveCircleOutlinedIcon
              sx={{ fontSize: "26px", color: colors.greenAccent[500] }}
            />
          </IconButton>
          <InputBase value={quantity} readOnly sx={{ flex: 1, minWidth: 40 }} />
          <IconButton
            onClick={incremet}
            disabled={!partName || !partPrice}
          >
            <AddCircleOutlinedIcon
              sx={{ fontSize: "26px", color: colors.greenAccent[500] }}
            />
          </IconButton>
        </div>

        <Box mx="0.2em" my={{ xs: 1, sm: 0 }}>
          Net Price:{" "}
          <strong>{(Math.round(totalPrice * 100) / 100).toFixed(2)}</strong>
        </Box>

        <ReactTooltip id="title required" effect="solid" place="top">
          {"Add to Cart"}
        </ReactTooltip>

        <IconButton
          className="cursor-pointer"
          disabled={quantity <= 0}
          onClick={addToCartHandler}
          data-tip
          data-for="title required"
        >
          <ShoppingCartIcon
            sx={{
              fontSize: "26px",
              color: quantity > 0 ? colors.greenAccent[500] : colors.grey[500],
            }}
          />
        </IconButton>
      </Box>
    </Box>
  );
};

export default CreateMiscPart;